// Input validation utilities for security and data integrity

/**
 * Sanitizes text input to prevent XSS and other security issues
 * @param input - Raw input string
 * @returns Sanitized string
 */
export const sanitizeInput = (input: string): string => {
  if (!input) return '';
  
  return input
    // Remove potentially dangerous characters
    .replace(/[<>]/g, '') // Remove HTML tags
    .replace(/javascript:/gi, '') // Remove javascript: protocol
    .replace(/on\w+\s*=/gi, '') // Remove event handlers
    .replace(/&[#\w]+;/gi, '') // Remove HTML entities
    // Remove excessive whitespace
    .replace(/\s+/g, ' ')
    .trim();
};

/**
 * Validates text input with basic security rules
 * @param input - Input string to validate
 * @param options - Validation options
 * @returns Validation result
 */
export const validateTextInput = (
  input: string, 
  options: {
    minLength?: number;
    maxLength?: number;
    allowSpaces?: boolean;
    allowNumbers?: boolean;
    allowSpecialChars?: boolean;
    pattern?: RegExp;
  } = {}
): { isValid: boolean; error?: string; sanitized: string } => {
  const {
    minLength = 1,
    maxLength = 255,
    allowSpaces = true,
    allowNumbers = true,
    allowSpecialChars = false,
    pattern = null
  } = options;

  const sanitized = sanitizeInput(input);

  // Check minimum length
  if (sanitized.length < minLength) {
    return {
      isValid: false,
      error: `Must be at least ${minLength} characters long`,
      sanitized
    };
  }

  // Check maximum length
  if (sanitized.length > maxLength) {
    return {
      isValid: false,
      error: `Must be no more than ${maxLength} characters long`,
      sanitized
    };
  }

  // Check allowed characters
  let allowedPattern = allowNumbers 
    ? /^[a-zA-Z0-9\s]*$/ 
    : /^[a-zA-Z\s]*$/;

  if (allowSpaces) {
    allowedPattern = allowNumbers
      ? /^[a-zA-Z0-9\s]*$/
      : /^[a-zA-Z\s]*$/;
  }

  if (allowSpecialChars) {
    allowedPattern = allowNumbers
      ? /^[a-zA-Z0-9\s\-_.,@#$%^&*()+=\[\]{}|\\:;"'<>?\/]*$/
      : /^[a-zA-Z\s\-_.,@#$%^&*()+=\[\]{}|\\:;"'<>?\/]*$/;
  }

  if (pattern) {
    allowedPattern = pattern;
  }

  if (!allowedPattern.test(sanitized)) {
    return {
      isValid: false,
      error: 'Contains invalid characters',
      sanitized
    };
  }

  return {
    isValid: true,
    sanitized
  };
};

/**
 * Validates email addresses
 * @param email - Email string to validate
 * @returns Validation result
 */
export const validateEmail = (email: string): { isValid: boolean; error?: string } => {
  const sanitized = sanitizeInput(email);
  const emailPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
  
  if (!emailPattern.test(sanitized)) {
    return {
      isValid: false,
      error: 'Please enter a valid email address'
    };
  }
  
  return { isValid: true };
};

/**
 * Validates phone numbers (US format)
 * @param phone - Phone string to validate
 * @returns Validation result
 */
export const validatePhone = (phone: string): { isValid: boolean; error?: string } => {
  const sanitized = sanitizeInput(phone);
  // Remove all non-digit characters for validation
  const digitsOnly = sanitized.replace(/\D/g, '');
  
  if (digitsOnly.length < 10) {
    return {
      isValid: false,
      error: 'Phone number must be at least 10 digits'
    };
  }
  
  if (digitsOnly.length > 15) {
    return {
      isValid: false,
      error: 'Phone number is too long'
    };
  }
  
  return { isValid: true };
};

/**
 * Validates numeric input
 * @param input - Numeric string to validate
 * @param options - Validation options
 * @returns Validation result
 */
export const validateNumeric = (
  input: string,
  options: {
    min?: number;
    max?: number;
    allowDecimals?: boolean;
  } = {}
): { isValid: boolean; error?: string; sanitized: string } => {
  const { min = 0, max = Number.MAX_SAFE_INTEGER, allowDecimals = false } = options;
  const sanitized = sanitizeInput(input);
  
  // Remove all non-numeric characters except decimal point if allowed
  const numericPattern = allowDecimals ? /^[0-9.]*$/ : /^[0-9]*$/;
  
  if (!numericPattern.test(sanitized)) {
    return {
      isValid: false,
      error: 'Please enter a valid number',
      sanitized
    };
  }
  
  const numValue = parseFloat(sanitized);
  
  if (isNaN(numValue)) {
    return {
      isValid: false,
      error: 'Please enter a valid number',
      sanitized
    };
  }
  
  if (numValue < min) {
    return {
      isValid: false,
      error: `Value must be at least ${min}`,
      sanitized
    };
  }
  
  if (numValue > max) {
    return {
      isValid: false,
      error: `Value must be no more than ${max}`,
      sanitized
    };
  }
  
  return {
    isValid: true,
    sanitized
  };
};

/**
 * Validates coupon codes
 * @param code - Coupon code to validate
 * @returns Validation result
 */
export const validateCouponCode = (code: string): { isValid: boolean; error?: string; sanitized: string } => {
  const sanitized = sanitizeInput(code);
  
  // Coupon codes should be alphanumeric, uppercase, no spaces
  const couponPattern = /^[A-Z0-9]*$/;
  
  if (!couponPattern.test(sanitized)) {
    return {
      isValid: false,
      error: 'Coupon codes can only contain letters and numbers (no spaces)',
      sanitized
    };
  }
  
  if (sanitized.length < 3) {
    return {
      isValid: false,
      error: 'Coupon code must be at least 3 characters long',
      sanitized
    };
  }
  
  if (sanitized.length > 20) {
    return {
      isValid: false,
      error: 'Coupon code must be no more than 20 characters long',
      sanitized
    };
  }
  
  return {
    isValid: true,
    sanitized
  };
};
